/*
 * Yadid Advisory shared document CSS builder.
 *
 * Emits the memo-quality typography + spacing stylesheet scoped to a
 * caller-supplied root id, so any surface can wrap its own body content
 * in a `<div id="…">` and get the reference typography for free.
 *
 * Rules included:
 *   · Georgia body + Helvetica caps eyebrows + navy headings + brass rules
 *   · Bullet + numbered list treatment matching Meeting Brief
 *   · Executive H1 / H2 / H3 hierarchy with break-after: avoid
 *   · Paragraph / list-item break-inside: avoid
 *   · Action Items: 22pt nested indent, 10.5pt semi-bold titles,
 *     .action-meta Helvetica Neue sub-line, checkbox slot
 *   · .keep-with-next helper for heading-to-content coupling
 */

import {
  YADID_FONT_SERIF,
  YADID_FONT_SANS,
  YADID_FONT_MONO,
  YADID_NAVY,
  YADID_BRASS,
  YADID_INK,
  YADID_META_LABEL,
  YADID_META_MUTED_INK,
} from "./tokens";

export function buildYadidDocCss(rootId) {
  const R = `#${rootId}`;
  return `
  ${R} { font-family:${YADID_FONT_SERIF}; color:${YADID_INK}; font-size:11.5pt; line-height:1.55; width:720px; padding:0; background:#ffffff; box-sizing:border-box; }
  ${R} .masthead { padding:8px 40px 16px 40px; }
  ${R} .masthead img { height:52px; width:auto; display:block; margin:0; }
  ${R} .rule-top { border-top:0.5pt solid ${YADID_BRASS}; margin:20px 40px 0 40px; }
  ${R} .meta { padding:14px 40px 22px 40px; display:table; width:calc(100% - 80px); table-layout:fixed; }
  ${R} .meta-cell { display:table-cell; vertical-align:top; padding-right:24px; }
  ${R} .meta-cell:last-child { padding-right:0; text-align:right; }
  ${R} .meta-label { font-family:${YADID_FONT_SANS}; font-size:7.5pt; letter-spacing:0.22em; text-transform:uppercase; color:${YADID_META_LABEL}; margin-bottom:4px; }
  ${R} .meta-value { font-family:${YADID_FONT_SANS}; font-size:9.5pt; color:${YADID_NAVY}; font-weight:600; letter-spacing:0.01em; }
  ${R} .body { padding:6px 40px 12px 40px; }
  ${R} h1 { font-size:20pt; font-weight:700; margin:0 0 14pt 0; letter-spacing:-0.01em; color:${YADID_NAVY}; line-height:1.2; page-break-after:avoid; break-after:avoid; }
  ${R} h2 { font-size:12.5pt; font-weight:700; margin:20pt 0 6pt 0; color:${YADID_NAVY}; letter-spacing:0.01em; page-break-after:avoid; break-after:avoid; page-break-inside:avoid; break-inside:avoid; }
  ${R} h3 { font-size:11pt; font-weight:700; margin:12pt 0 4pt 0; color:${YADID_NAVY}; page-break-after:avoid; break-after:avoid; page-break-inside:avoid; break-inside:avoid; }
  ${R} p  { margin:0 0 9pt 0; page-break-inside:avoid; break-inside:avoid; }
  ${R} strong { font-weight:700; color:${YADID_NAVY}; }
  ${R} em { font-style:italic; }
  ${R} code { font-family:${YADID_FONT_MONO}; font-size:10pt; background:#F5F5F4; padding:1pt 3pt; }
  ${R} hr { border:none; border-top:0.5pt solid #d4d4d4; margin:10pt 0; }
  ${R} a { color:${YADID_NAVY}; text-decoration:underline; }
  ${R} blockquote { border-left:2px solid ${YADID_BRASS}; padding:2pt 12pt; margin:9pt 0; color:${YADID_META_MUTED_INK}; font-style:italic; page-break-inside:avoid; break-inside:avoid; }
  ${R} .doc-list, ${R} .doc-list-num { list-style:none; padding:0; margin:0 0 10pt 0; }
  ${R} .doc-list > li,
  ${R} .doc-list-num > li { position:relative; padding:0 0 4pt 22pt; margin:0; line-height:1.55; page-break-inside:avoid; break-inside:avoid; }
  ${R} .li-dot { position:absolute; left:6pt; top:0; color:${YADID_BRASS}; font-weight:700; font-size:13pt; line-height:1.55; }
  ${R} .li-num { position:absolute; left:0; top:1pt; color:${YADID_BRASS}; font-family:${YADID_FONT_SANS}; font-weight:700; font-size:9.5pt; letter-spacing:0.02em; }
  ${R} .keep-with-next { page-break-inside:avoid; break-inside:avoid; }
  ${R} p.action-item { margin:0 0 10pt 0; padding-left:22pt; position:relative; page-break-inside:avoid; break-inside:avoid; }
  ${R} p.action-item strong { font-weight:600; font-size:10.5pt; color:${YADID_NAVY}; letter-spacing:0.005em; }
  ${R} p.action-item .action-meta { display:block; margin-top:2pt; font-family:${YADID_FONT_SANS}; font-size:8.75pt; font-weight:400; color:${YADID_META_MUTED_INK}; letter-spacing:0.01em; }
  ${R} p.action-item .action-check { position:absolute; left:9pt; top:5pt; width:8.5pt; height:8.5pt; border:0.75pt solid ${YADID_NAVY}; background:#ffffff; box-sizing:border-box; display:block; }
`;
}
